/**
 * Nombre legible del tipo de entidad de una notificación.
 *
 * <p><b>Qué arregla.</b> Tanto el desplegable de la campana como el centro de notificaciones
 * pintaban el `entityType` <b>tal cual llega del servidor</b>: `comment_action`, `chat_message`,
 * `wiki_page`. QA lo reportó como texto en inglés, pero no es texto — es el identificador técnico
 * del backend asomando en la interfaz. Por eso no aparecía en ninguna búsqueda de literales: no
 * hay ningún «action item» escrito en el código del frontend, lo trae el dato.
 *
 * <p><b>Por qué vive en `shared` y no junto a las notificaciones de NEXUS.</b> Los dos sitios que
 * lo pintan están en librerías distintas —una de NEXUS y otra de PartnerCore— y hacer que la de
 * PartnerCore importe de NEXUS invertiría la dirección de dependencia del producto. Aquí lo pueden
 * usar las dos sin conocerse.
 *
 * <p><b>Un tipo desconocido cae en la etiqueta genérica, no se enseña.</b> Es deliberado: enseñar
 * el identificador «sólo cuando no lo conocemos» es exactamente el defecto que se está corrigiendo,
 * y reaparecería en cuanto el backend añadiera un tipo nuevo. Si falta alguno, lo que hay que hacer
 * es añadir su clave aquí.
 */
const ENTITY_TYPE_KEYS: Record<string, string> = {
  task:           'notifications.entityType.task',
  comment:        'notifications.entityType.comment',
  comment_action: 'notifications.entityType.commentAction',
  space:          'notifications.entityType.space',
  ticket:         'notifications.entityType.ticket',
  meeting:        'notifications.entityType.meeting',
  chat_channel:   'notifications.entityType.chatChannel',
  chat_message:   'notifications.entityType.chatMessage',
  clip:           'notifications.entityType.clip',
  wiki_page:      'notifications.entityType.wikiPage',
  pipeline:       'notifications.entityType.pipeline',
  deal:           'notifications.entityType.deal',
  account:        'notifications.entityType.account',
  contact:        'notifications.entityType.contact',
  budget:         'notifications.entityType.budget',
  expense:        'notifications.entityType.expense',
  invoice:        'notifications.entityType.invoice',
  booking:        'notifications.entityType.booking',
  automation:     'notifications.entityType.automation',
  environment:    'notifications.entityType.environment',
  report:         'notifications.entityType.report',
};

/** Clave i18n del tipo, o la genérica si no se reconoce. Nunca devuelve el identificador crudo. */
export function notificationEntityTypeKey(entityType: string | null | undefined): string {
  if (!entityType) return 'notifications.entityType.default';
  return ENTITY_TYPE_KEYS[entityType] ?? 'notifications.entityType.default';
}

/**
 * Nombre del módulo de origen de una notificación.
 *
 * <p>El servidor manda el código del catálogo —`M01`, `M17`— y se pintaba tal cual, como una
 * etiqueta junto al aviso y en su detalle. Es el mismo defecto que el tipo de entidad: un
 * identificador interno asomando en la interfaz, que a quien lo lee no le dice nada.
 *
 * <p>Se traduce en el cliente y no en el servidor porque el código <b>es</b> el dato —identifica al
 * módulo, no a su nombre— y el nombre visible ya se traduce en el cliente para el resto del
 * producto (el menú lateral hace lo mismo con `sidebar.byUrl`).
 */
const SOURCE_MODULE_KEYS: Record<string, string> = {
  M01: 'notifications.sourceModule.m01',
  M14: 'notifications.sourceModule.m14',
  M15: 'notifications.sourceModule.m15',
  M16: 'notifications.sourceModule.m16',
  M17: 'notifications.sourceModule.m17',
};

/**
 * Clave i18n del módulo, o cadena vacía si no se reconoce.
 *
 * <p>Devuelve vacío —y no el código— a propósito: quien pinta esto lo hace en una etiqueta
 * decorativa, y una etiqueta ausente informa más que una que diga «M23».
 */
export function notificationSourceModuleKey(sourceModule: string | null | undefined): string {
  if (!sourceModule) return '';
  return SOURCE_MODULE_KEYS[sourceModule] ?? '';
}
